import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Copy, 
  TrendingUp, 
  Users, 
  Award, 
  DollarSign, 
  BarChart3, 
  Star, 
  CheckCircle,
  ArrowRight,
  Target,
  Shield,
  Clock,
  Globe
} from 'lucide-react';

// Import professional images
const copyTradingImage = require('../../assets/traders-profit-279315.jpg');
const tradingTeamImage = require('../../assets/team-investors-analyzing-profit-amount-making-decisions_482257-88779.jpg_uiwnmx.jpg');
const professionalTraderImage = require('../../assets/businesswoman-bitcoin.jpg');

const CopyTrading = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState('1month');

  const topTraders = [
    {
      id: 1,
      name: 'Sarah Mitchell',
      title: 'Professional Stock Trader',
      avatar: '👩‍💼',
      followers: '12,847',
      winRate: '89%',
      totalReturn: '+247%',
      monthlyReturn: '+18.3%',
      riskScore: 'Medium',
      tradingStyle: 'Growth Stocks',
      performance: {
        '1week': '+4.2%',
        '1month': '+18.3%',
        '3months': '+52.7%',
        '1year': '+247%'
      },
      recentTrades: [
        { symbol: 'AAPL', profit: '+12.5%', time: '2 hours ago' },
        { symbol: 'MSFT', profit: '+8.7%', time: '1 day ago' },
        { symbol: 'GOOGL', profit: '+15.2%', time: '3 days ago' }
      ]
    },
    {
      id: 2,
      name: 'Marcus Chen',
      title: 'Crypto Specialist',
      avatar: '👨‍💻',
      followers: '8,932',
      winRate: '92%',
      totalReturn: '+387%',
      monthlyReturn: '+25.1%',
      riskScore: 'High',
      tradingStyle: 'Cryptocurrency',
      performance: {
        '1week': '+7.8%',
        '1month': '+25.1%',
        '3months': '+89.4%',
        '1year': '+387%'
      },
      recentTrades: [
        { symbol: 'BTC', profit: '+22.3%', time: '4 hours ago' },
        { symbol: 'ETH', profit: '+18.9%', time: '6 hours ago' },
        { symbol: 'SOL', profit: '+31.5%', time: '1 day ago' }
      ]
    },
    {
      id: 3,
      name: 'David Rodriguez',
      title: 'Conservative Investor',
      avatar: '👨‍💼',
      followers: '15,623',
      winRate: '76%',
      totalReturn: '+156%',
      monthlyReturn: '+11.2%',
      riskScore: 'Low',
      tradingStyle: 'Dividend Stocks',
      performance: {
        '1week': '+2.1%',
        '1month': '+11.2%',
        '3months': '+28.9%',
        '1year': '+156%'
      },
      recentTrades: [
        { symbol: 'JNJ', profit: '+6.4%', time: '1 day ago' },
        { symbol: 'KO', profit: '+4.2%', time: '2 days ago' },
        { symbol: 'PG', profit: '+5.8%', time: '3 days ago' }
      ]
    }
  ];

  const copyTradingBenefits = [
    {
      icon: <Target className="h-8 w-8 text-emerald-600" />,
      title: 'Learn from Experts',
      description: 'Follow proven strategies from professional traders with verified track records.',
      highlight: 'Access to 500+ verified traders'
    },
    {
      icon: <Clock className="h-8 w-8 text-blue-600" />,
      title: 'Save Time',
      description: 'No need to spend hours analyzing markets. Let experts do the work for you.',
      highlight: 'Automated 24/7 trading'
    },
    {
      icon: <Shield className="h-8 w-8 text-purple-600" />,
      title: 'Risk Management',
      description: 'Built-in risk controls and stop-loss mechanisms protect your investments.',
      highlight: 'Advanced risk controls'
    },
    {
      icon: <BarChart3 className="h-8 w-8 text-orange-600" />,
      title: 'Diversification',
      description: 'Copy multiple traders across different markets and strategies for better diversification.',
      highlight: 'Multi-strategy approach'
    }
  ];

  const copyTradingSteps = [
    {
      step: 1,
      title: 'Choose Your Trader',
      description: 'Browse our leaderboard of top-performing traders and select those that match your investment goals and risk tolerance.',
      icon: '🔍'
    },
    {
      step: 2,
      title: 'Set Your Investment',
      description: 'Decide how much you want to allocate to each trader. You can start with as little as $100.',
      icon: '💰'
    },
    {
      step: 3,
      title: 'Start Copying',
      description: 'Your account will automatically mirror the trades of your selected traders in real-time.',
      icon: '🔄'
    },
    {
      step: 4,
      title: 'Monitor & Adjust',
      description: 'Track your performance and adjust your copy trading portfolio whenever you want.',
      icon: '📊'
    }
  ];

  const stats = [
    { number: '50K+', label: 'Copy Traders', icon: Users },
    { number: '500+', label: 'Expert Traders', icon: Award },
    { number: '$2.5B+', label: 'Copied Volume', icon: DollarSign },
    { number: '78%', label: 'Success Rate', icon: TrendingUp }
  ];

  return (
    <div className="copy-trading-page">
      {/* Hero Section */}
      <section className="copy-trading-hero">
        <div className="copy-trading-hero-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="copy-trading-hero-content"
            >
              <div className="copy-trading-hero-badge">
                <Copy className="copy-trading-hero-badge-icon" />
                <span>Copy Trading</span>
              </div>
              <h1 className="copy-trading-hero-title">
                Copy Successful Traders,
                <span className="copy-trading-hero-title-gradient"> Earn Like a Pro</span>
              </h1>
              <p className="copy-trading-hero-subtitle">
                Mirror the trades of our top-performing traders automatically. Start earning 
                from day one without needing years of trading experience.
              </p>
              
              <div className="copy-trading-hero-stats">
                <div className="copy-trading-hero-stat">
                  <div className="copy-trading-hero-stat-number">247%</div>
                  <div className="copy-trading-hero-stat-label">Avg. Annual Return</div>
                </div>
                <div className="copy-trading-hero-stat">
                  <div className="copy-trading-hero-stat-number">89%</div>
                  <div className="copy-trading-hero-stat-label">Success Rate</div>
                </div>
                <div className="copy-trading-hero-stat">
                  <div className="copy-trading-hero-stat-number">$100</div>
                  <div className="copy-trading-hero-stat-label">Min. Investment</div>
                </div>
              </div>

              <div className="copy-trading-hero-buttons">
                <motion.a
                  href="https://dashboard.chartlinebrokerage.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="copy-trading-hero-cta-primary"
                >
                  Start Copy Trading
                  <ArrowRight className="h-5 w-5 ml-2" />
                </motion.a>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="copy-trading-hero-cta-secondary"
                >
                  Browse Top Traders
                </motion.button>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="copy-trading-hero-image"
            >
              <div className="relative">
                <img
                  src={copyTradingImage}
                  alt="Copy Trading Success"
                  className="w-full h-auto rounded-2xl shadow-2xl"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/20 to-transparent rounded-2xl" />
                
                {/* Floating Performance Card */}
                <div className="absolute top-6 right-6 bg-white/95 backdrop-blur-sm rounded-xl p-4 shadow-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                    <span className="text-sm font-semibold text-slate-800">Live Trading</span>
                  </div>
                  <div className="text-2xl font-bold text-emerald-600">+18.3%</div>
                  <div className="text-xs text-slate-600">Monthly Return</div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* How Copy Trading Works */}
      <section className="copy-trading-how-section">
        <div className="copy-trading-how-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="copy-trading-how-header"
          >
            <h2 className="copy-trading-how-title">How Copy Trading Works</h2>
            <p className="copy-trading-how-subtitle">
              Start earning from expert traders in 4 simple steps
            </p>
          </motion.div>

          <div className="copy-trading-steps-grid">
            {copyTradingSteps.map((step, index) => (
              <motion.div
                key={step.step}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="copy-trading-step-card"
              >
                <div className="copy-trading-step-number">{step.step}</div>
                <div className="copy-trading-step-icon">{step.icon}</div>
                <h3 className="copy-trading-step-title">{step.title}</h3>
                <p className="copy-trading-step-description">{step.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Top Traders Section */}
      <section className="copy-trading-traders-section">
        <div className="copy-trading-traders-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="copy-trading-traders-header"
          >
            <h2 className="copy-trading-traders-title">Top Performing Traders</h2>
            <p className="copy-trading-traders-subtitle">
              Join thousands of investors already copying these expert traders
            </p>
            
            <div className="copy-trading-timeframe-selector">
              {['1week', '1month', '3months', '1year'].map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => setSelectedTimeframe(timeframe)}
                  className={`copy-trading-timeframe-btn ${selectedTimeframe === timeframe ? 'active' : ''}`}
                >
                  {timeframe === '1week' && '1 Week'}
                  {timeframe === '1month' && '1 Month'}
                  {timeframe === '3months' && '3 Months'}
                  {timeframe === '1year' && '1 Year'}
                </button>
              ))}
            </div>
          </motion.div>

          <div className="copy-trading-traders-grid">
            {topTraders.map((trader, index) => (
              <motion.div
                key={trader.id}
                initial={{ opacity: 0, y: 40 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="copy-trading-trader-card"
              >
                <div className="copy-trading-trader-header">
                  <div className="copy-trading-trader-avatar">{trader.avatar}</div>
                  <div className="copy-trading-trader-info">
                    <h3 className="copy-trading-trader-name">{trader.name}</h3>
                    <p className="copy-trading-trader-title">{trader.title}</p>
                    <div className="copy-trading-trader-followers">
                      <Users className="h-4 w-4" />
                      {trader.followers} followers
                    </div>
                  </div>
                  <div className="copy-trading-trader-performance">
                    <div className="copy-trading-performance-main">
                      {trader.performance[selectedTimeframe]}
                    </div>
                    <div className="copy-trading-performance-label">
                      {selectedTimeframe === '1week' && 'Weekly'}
                      {selectedTimeframe === '1month' && 'Monthly'}
                      {selectedTimeframe === '3months' && '3M Return'}
                      {selectedTimeframe === '1year' && 'Annual Return'}
                    </div>
                  </div>
                </div>

                <div className="copy-trading-trader-stats">
                  <div className="copy-trading-stat">
                    <span className="copy-trading-stat-label">Win Rate</span>
                    <span className="copy-trading-stat-value">{trader.winRate}</span>
                  </div>
                  <div className="copy-trading-stat">
                    <span className="copy-trading-stat-label">Risk Level</span>
                    <span className={`copy-trading-risk-badge ${trader.riskScore.toLowerCase()}`}>
                      {trader.riskScore}
                    </span>
                  </div>
                  <div className="copy-trading-stat">
                    <span className="copy-trading-stat-label">Style</span>
                    <span className="copy-trading-stat-value">{trader.tradingStyle}</span>
                  </div>
                </div>

                <div className="copy-trading-recent-trades">
                  <h4 className="copy-trading-recent-title">Recent Trades</h4>
                  {trader.recentTrades.map((trade, idx) => (
                    <div key={idx} className="copy-trading-trade-item">
                      <span className="copy-trading-trade-symbol">{trade.symbol}</span>
                      <span className="copy-trading-trade-profit">{trade.profit}</span>
                      <span className="copy-trading-trade-time">{trade.time}</span>
                    </div>
                  ))}
                </div>

                <motion.a
                  href="https://dashboard.chartlinebrokerage.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="copy-trading-copy-button"
                >
                  Copy This Trader
                </motion.a>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="copy-trading-benefits-section">
        <div className="copy-trading-benefits-container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            
            {/* Left Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="copy-trading-benefits-image"
            >
              <img
                src={tradingTeamImage}
                alt="Professional Trading Team"
                className="w-full h-auto rounded-2xl shadow-xl"
              />
            </motion.div>

            {/* Right Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="copy-trading-benefits-content"
            >
              <h2 className="copy-trading-benefits-title">
                Why Choose <span className="text-emerald-600">Copy Trading</span>?
              </h2>
              <p className="copy-trading-benefits-subtitle">
                Transform your investment strategy with the power of social trading and expert knowledge.
              </p>
              
              <div className="copy-trading-benefits-grid">
                {copyTradingBenefits.map((benefit, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                    className="copy-trading-benefit-item"
                  >
                    <div className="copy-trading-benefit-icon">
                      {benefit.icon}
                    </div>
                    <div className="copy-trading-benefit-content">
                      <h4 className="copy-trading-benefit-title">{benefit.title}</h4>
                      <p className="copy-trading-benefit-description">{benefit.description}</p>
                      <span className="copy-trading-benefit-highlight">{benefit.highlight}</span>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="copy-trading-stats-section">
        <div className="copy-trading-stats-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="copy-trading-stats-header"
          >
            <h2 className="copy-trading-stats-title">
              Join the Copy Trading Revolution
            </h2>
            <p className="copy-trading-stats-subtitle">
              Trusted by thousands of investors worldwide
            </p>
          </motion.div>

          <div className="copy-trading-stats-grid">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="copy-trading-stat-card"
              >
                <div className="copy-trading-stat-icon">
                  <stat.icon className="h-12 w-12 text-emerald-600" />
                </div>
                <div className="copy-trading-stat-content">
                  <div className="copy-trading-stat-number">{stat.number}</div>
                  <div className="copy-trading-stat-label">{stat.label}</div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="copy-trading-cta-section">
        <div className="copy-trading-cta-container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="copy-trading-cta-content"
          >
            <h2 className="copy-trading-cta-title">
              Ready to Start Copy Trading?
            </h2>
            <p className="copy-trading-cta-subtitle">
              Join thousands of successful investors who are already earning with copy trading. 
              Start with just $100 and watch your portfolio grow.
            </p>
            
            <div className="copy-trading-cta-features">
              <div className="copy-trading-cta-feature">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <span>No hidden fees</span>
              </div>
              <div className="copy-trading-cta-feature">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <span>Start with $100</span>
              </div>
              <div className="copy-trading-cta-feature">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <span>Withdraw anytime</span>
              </div>
              <div className="copy-trading-cta-feature">
                <CheckCircle className="h-5 w-5 text-emerald-500" />
                <span>24/7 support</span>
              </div>
            </div>

            <motion.a
              href="https://dashboard.chartlinebrokerage.com"
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="copy-trading-cta-button"
            >
              Start Copy Trading Now
              <ArrowRight className="h-5 w-5 ml-2" />
            </motion.a>
            
            <p className="copy-trading-cta-disclaimer">
              * Past performance does not guarantee future results. All trading involves risk.
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CopyTrading; 